import * as React from "react";
import { Navigate, Outlet } from "react-router-dom";

import { ErrorPanel } from "@/components/common/ErrorPanel";
import { LoadingSkeleton } from "@/components/common/LoadingSkeleton";
import { roleAtLeast, useMe } from "./RequireAuth";
import type { Role } from "@/lib/api/types";

export interface RequireRoleProps {
  /** The lowest role that may see this subtree. Roles rank reader < admin. */
  min: Role;
  children?: React.ReactNode;
}

/**
 * Gate: the portal user's role must be at least `min`.
 *
 * The role is read from /auth/me - the portal_users row - and not from a token
 * claim, so revoking an admin takes effect immediately rather than when their
 * access token happens to expire.
 *
 * The matching NAVIGATION is hidden too: AppShell renders the Administration
 * group only for admins. sp-migrate shows its Settings item to everyone and
 * bounces the click to /403, which teaches operators that the product is
 * broken rather than that the item is not theirs.
 */
export function RequireRole({ min, children }: RequireRoleProps): React.ReactElement {
  const me = useMe();

  if (me.isPending) {
    return <LoadingSkeleton variant="page" label="Checking your access" />;
  }
  if (me.isError) {
    return (
      <div className="p-6">
        <ErrorPanel
          action="check your access"
          error={me.error}
          onRetry={() => {
            void me.refetch();
          }}
        />
      </div>
    );
  }
  if (!roleAtLeast(me.data.role, min)) {
    return <Navigate to="/403" replace state={{ required: min, actual: me.data.role }} />;
  }
  return <>{children ?? <Outlet />}</>;
}
